import {
	CodelistRecord,
	CodelistRecordKey,
	createCodelistRecordSchema
} from 'features/codelist/domain/codelistRecordSchema';
import { LanguageType } from 'types';
import { ImportedFileFieldNameByCodelistRecordFieldName } from '../../ImportCodelistPage';

export type RowErrorType =
	| 'invalid-phone'
	| 'unique-conflict'
	| 'invalid-email'
	| 'invalid-date-of-birth'
	| 'missing-custom-id';

// For backward compatibility
export type RowError = RowErrorType;

export type RowErrorDetail = {
	rowIndex: number; // 0-based index
	rowNumber: number; // 1-based for display (includes header: rowIndex + 2)
	errorType: RowErrorType;
	fieldName: CodelistRecordKey;
	fieldValue?: string;
	customId?: string; // For context
};

export type ParseErrorsResult = {
	errors: Set<RowErrorType>; // Keep for backward compatibility
	errorDetails: RowErrorDetail[]; // NEW: detailed row-specific errors
	parsedRecords: CodelistRecord[];
	hasFixablePhoneErrors: boolean; // NEW: distinguish fixable phone errors
};

export const parseErrors = (args: {
	rows: Record<string, string>[];
	language: LanguageType;
	uniqueField: string;
	importedFileFieldNamesByCodelistRecordFieldNames: Partial<ImportedFileFieldNameByCodelistRecordFieldName>;
	prependCountryCode?: string;
}): ParseErrorsResult => {
	const codelistSchema = createCodelistRecordSchema(args.language);

	const errors = new Set<RowErrorType>();
	const errorDetails: RowErrorDetail[] = [];
	const customIds = new Set<string>();
	const customIdFirstOccurrence = new Map<string, number>(); // Track first occurrence of each customId

	const parsedRecords: CodelistRecord[] = [];
	let hasFixablePhoneErrors = false;

	for (let rowIndex = 0; rowIndex < args.rows.length; rowIndex++) {
		const row = args.rows[rowIndex];
		const rowNumber = rowIndex + 2; // Account for 0-based index + header row
		const codelistRecord: CodelistRecord = {
			dateOfBirth: undefined,
			customId: row[args.uniqueField]
		};

		for (const [codelistField, importedField] of Object.entries(
			args.importedFileFieldNamesByCodelistRecordFieldNames
		)) {
			let value = row[importedField];

			// Apply country code prefix to phone numbers if specified
			if (codelistField === 'mobilePhone' && value) {
				// Convert 00 international prefix to + format
				if (value.startsWith('00') && /^00\d/.test(value)) {
					value = '+' + value.slice(2);
				}
				// Prepend country code if specified and number doesn't have one
				else if (args.prependCountryCode && !value.startsWith('+')) {
					value = args.prependCountryCode + value;
				}
			}

			codelistRecord[codelistField as keyof CodelistRecord] = value;
		}

		const validated = codelistSchema.safeParse(codelistRecord);

		if (validated.success) {
			parsedRecords.push(validated.data);
		}

		if (!validated.success) {
			// Check for phone number errors
			const phoneIssue = validated.error.issues.find(issue =>
				issue.path.includes('mobilePhone')
			);
			if (phoneIssue) {
				errors.add('invalid-phone');
				const phoneValue = codelistRecord.mobilePhone;

				// Check if the phone error is fixable (just missing country code)
				// A phone is fixable if it doesn't start with + but is otherwise valid format
				const isFixable =
					phoneValue &&
					!phoneValue.startsWith('+') &&
					!phoneValue.startsWith('00') &&
					/^\d{8,15}$/.test(phoneValue); // Basic digit-only check

				if (isFixable) {
					hasFixablePhoneErrors = true;
				}

				errorDetails.push({
					rowIndex,
					rowNumber,
					errorType: 'invalid-phone',
					fieldName: 'mobilePhone',
					fieldValue: phoneValue,
					customId: codelistRecord.customId
				});
			}

			// Check for email errors
			const emailIssue = validated.error.issues.find(issue => issue.path.includes('email'));
			if (emailIssue) {
				errors.add('invalid-email');
				errorDetails.push({
					rowIndex,
					rowNumber,
					errorType: 'invalid-email',
					fieldName: 'email',
					fieldValue: codelistRecord.email,
					customId: codelistRecord.customId
				});
			}

			// Check for date of birth errors
			const dobIssue = validated.error.issues.find(issue =>
				issue.path.includes('dateOfBirth')
			);
			if (dobIssue) {
				errors.add('invalid-date-of-birth');
				errorDetails.push({
					rowIndex,
					rowNumber,
					errorType: 'invalid-date-of-birth',
					fieldName: 'dateOfBirth',
					fieldValue: codelistRecord.dateOfBirth,
					customId: codelistRecord.customId
				});
			}
		}

		// Ensure custom ids are unique
		const customIdInRow = codelistRecord.customId;

		if (!customIdInRow) {
			errors.add('missing-custom-id');
			errorDetails.push({
				rowIndex,
				rowNumber,
				errorType: 'missing-custom-id',
				fieldName: 'customId',
				fieldValue: customIdInRow,
				customId: customIdInRow
			});
			continue;
		}

		// Track first occurrence of each customId
		if (!customIdFirstOccurrence.has(customIdInRow)) {
			customIdFirstOccurrence.set(customIdInRow, rowIndex);
		} else if (customIds.has(customIdInRow)) {
			// This is a duplicate (not the first occurrence)
			errors.add('unique-conflict');
			errorDetails.push({
				rowIndex,
				rowNumber,
				errorType: 'unique-conflict',
				fieldName: 'customId',
				fieldValue: customIdInRow,
				customId: customIdInRow
			});
		}
		customIds.add(customIdInRow);
	}

	return { errors, errorDetails, parsedRecords, hasFixablePhoneErrors };
};
